import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, Save, CheckCircle2, Copy } from 'lucide-react';
import RouterOSVersionSelector from '../components/RouterOSVersionSelector';
import { generateBalanceadorScript } from '../lib/generators/balanceador';
import { saveToHistory } from '../lib/storage/database';

const BalanceadorWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generatedScript, setGeneratedScript] = useState('');
  
  const [formData, setFormData] = useState({
    tipo: 'nth', // nth or pcc
    numWans: 2,
    wans: [
      { puerto: 'ether1', tipo: 'dhcp', ip: '', gateway: '', netmask: '255.255.255.0' },
      { puerto: 'ether2', tipo: 'dhcp', ip: '', gateway: '', netmask: '255.255.255.0' }
    ],
    router: {
      nombre: '',
      zona_horaria: 'America/Mexico_City',
      subnet_lan: '192.168.0.0/24',
      dns_servers: ['8.8.8.8', '8.8.4.4']
    },
    routeros_version: 'v6'
  });

  const totalSteps = 6; // Cambiado de 5 a 6 (separamos versión y tipo)

  const updateWanCount = (count) => {
    const newWans = [];
    for (let i = 0; i < count; i++) {
      if (formData.wans[i]) {
        newWans.push(formData.wans[i]);
      } else {
        newWans.push({
          puerto: `ether${i + 1}`,
          tipo: 'dhcp',
          ip: '',
          gateway: '',
          netmask: '255.255.255.0'
        });
      }
    }
    setFormData({ ...formData, numWans: count, wans: newWans });
  };

  const updateWan = (index, field, value) => {
    const newWans = [...formData.wans];
    newWans[index] = { ...newWans[index], [field]: value };
    setFormData({ ...formData, wans: newWans });
  };

  const generateScript = async () => {
    setLoading(true);
    try {
      // Preparar configuración para el generador
      const config = {
        tipo: formData.tipo,
        wans: formData.wans.map(wan => ({
          puerto: wan.puerto,
          tipo: wan.tipo,
          ip: wan.ip || '',
          gateway: wan.gateway || '',
          netmask: wan.netmask || '255.255.255.0'
        })),
        router: {
          nombre: formData.router.nombre,
          zonaHoraria: formData.router.zona_horaria,
          subnetLan: formData.router.subnet_lan,
          dnsServers: formData.router.dns_servers
        },
        routerosVersion: formData.routeros_version
      };
      
      // Generar script localmente (sin backend)
      const script = generateBalanceadorScript(config);
      
      setGeneratedScript(script);
      
      // Guardar en historial local
      saveToHistory({
        script,
        type: 'balanceador',
        config,
        nombre: `Balanceador ${formData.tipo.toUpperCase()} - ${formData.numWans} WANs`
      });
      
      setCurrentStep(6);
      
      // Auto-copiar
      copyToClipboard(script);
      alert('✅ Script generado y copiado al portapapeles');
    } catch (error) {
      console.error('Error generating script:', error);
      alert('❌ Error al generar el script: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('✅ Script copiado al portapapeles');
    }).catch(err => {
      console.error('Error al copiar:', err);
      alert('❌ Error al copiar el script');
    });
  };

  const downloadScript = () => {
    const blob = new Blob([generatedScript], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `balanceador_${formData.tipo}_${formData.numWans}wan_${Date.now()}.rsc`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === totalSteps - 1) {
      generateScript();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.routeros_version !== '';
      case 2:
        return formData.tipo !== '';
      case 3:
        return formData.numWans >= 2 && formData.numWans <= 8;
      case 4:
        return formData.wans.every(wan => {
          if (wan.tipo === 'fijo') {
            return wan.puerto && wan.ip && wan.gateway;
          }
          return wan.puerto;
        });
      case 5:
        return formData.router.nombre !== '';
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/scripts')}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </button>
          
          <h1 className="text-3xl font-bold text-white">
            Configuración Balanceador
          </h1>
          
          <div className="w-20"></div>
        </div>

        {/* Progress Bar */}
        {currentStep < 6 && (
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              {[1, 2, 3, 4, 5].map((step) => (
                <div
                  key={step}
                  className={`flex items-center ${step < 5 ? 'flex-1' : ''}`}
                >
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-bold
                      ${currentStep >= step
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-700 text-gray-400'
                      }
                    `}
                  >
                    {step}
                  </div>
                  {step < 5 && (
                    <div
                      className={`
                        flex-1 h-1 mx-2
                        ${currentStep > step ? 'bg-blue-500' : 'bg-gray-700'}
                      `}
                    ></div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span>Versión</span>
              <span>Tipo</span>
              <span>WANs</span>
              <span>Config</span>
              <span>Router</span>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-gray-800 rounded-lg p-8 shadow-xl">
          {/* Step 1: Selección de Versión RouterOS */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 1: Versión de RouterOS
              </h2>
              
              <p className="text-gray-300 mb-6">
                Selecciona la versión de RouterOS que tiene tu equipo MikroTik
              </p>

              <RouterOSVersionSelector
                value={formData.routeros_version}
                onChange={(version) => setFormData({ ...formData, routeros_version: version })}
              />
            </div>
          )}

          {/* Step 2: Tipo de Balanceo */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 2: Tipo de Balanceo
              </h2>
              
              <p className="text-gray-300 mb-6">
                Selecciona el método de balanceo de carga que deseas utilizar
              </p>
                
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setFormData({ ...formData, tipo: 'nth' })}
                  data-testid="tipo-nth-btn"
                  className={`
                    p-6 rounded-lg border-2 transition-all
                    ${formData.tipo === 'nth'
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-gray-600 hover:border-gray-500'
                    }
                  `}
                >
                  <h3 className="text-xl font-bold text-white mb-2">NTH</h3>
                  <p className="text-sm text-gray-300">
                    Balanceo por paquetes secuenciales. Simple y efectivo.
                  </p>
                </button>

                <button
                  onClick={() => setFormData({ ...formData, tipo: 'pcc' })}
                  data-testid="tipo-pcc-btn"
                  className={`
                    p-6 rounded-lg border-2 transition-all
                    ${formData.tipo === 'pcc'
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-gray-600 hover:border-gray-500'
                    }
                  `}
                >
                  <h3 className="text-xl font-bold text-white mb-2">PCC</h3>
                  <p className="text-sm text-gray-300">
                    Per Connection Classifier. Mantiene la conexión en la misma WAN.
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Cantidad de WANs */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 3: ¿Cuántos WANs?
              </h2>
              
              <p className="text-gray-300 mb-6">
                Selecciona la cantidad de conexiones WAN que deseas balancear
              </p>

              <div className="grid grid-cols-4 gap-3">
                {[2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <button
                    key={num}
                    onClick={() => updateWanCount(num)}
                    data-testid={`wan-count-${num}`}
                    className={`
                      p-4 rounded-lg border-2 transition-all
                      ${formData.numWans === num
                        ? 'border-blue-500 bg-blue-500/20'
                        : 'border-gray-600 hover:border-gray-500'
                      }
                    `}
                  >
                    <div className="text-2xl font-bold text-white">{num}</div>
                    <div className="text-xs text-gray-300">WANs</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Configurar cada WAN */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 4: Configurar WANs
              </h2>
              
              <p className="text-gray-300 mb-4">
                Configura cada una de tus conexiones WAN
              </p>

              <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                {formData.wans.map((wan, index) => (
                  <div key={index} className="bg-gray-700 p-4 rounded-lg">
                    <h3 className="text-lg font-bold text-white mb-3">
                      WAN {index + 1}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-300 mb-2">
                          Puerto
                        </label>
                        <select
                          value={wan.puerto}
                          onChange={(e) => updateWan(index, 'puerto', e.target.value)}
                          data-testid={`wan-${index}-puerto`}
                          className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                            <option key={num} value={`ether${num}`}>
                              ether{num}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-300 mb-2">
                          Tipo
                        </label>
                        <select
                          value={wan.tipo}
                          onChange={(e) => updateWan(index, 'tipo', e.target.value)}
                          data-testid={`wan-${index}-tipo`}
                          className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg"
                        >
                          <option value="dhcp">DHCP</option>
                          <option value="fijo">IP Fija</option>
                        </select>
                      </div>

                      {wan.tipo === 'fijo' && (
                        <>
                          <div>
                            <label className="block text-sm text-gray-300 mb-2">
                              IP
                            </label>
                            <input
                              type="text"
                              value={wan.ip}
                              onChange={(e) => updateWan(index, 'ip', e.target.value)}
                              data-testid={`wan-${index}-ip`}
                              placeholder="192.168.1.25"
                              className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg"
                            />
                          </div>

                          <div>
                            <label className="block text-sm text-gray-300 mb-2">
                              Gateway
                            </label>
                            <input
                              type="text"
                              value={wan.gateway}
                              onChange={(e) => updateWan(index, 'gateway', e.target.value)}
                              data-testid={`wan-${index}-gateway`}
                              placeholder="192.168.1.254"
                              className="w-full px-3 py-2 bg-gray-600 text-white rounded-lg"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Datos del Router */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Paso 5: Datos del Router
              </h2>
              
              <p className="text-gray-300 mb-4">
                Información general del router MikroTik
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    Nombre del Router *
                  </label>
                  <input
                    type="text"
                    value={formData.router.nombre}
                    onChange={(e) => setFormData({
                      ...formData,
                      router: { ...formData.router, nombre: e.target.value }
                    })}
                    data-testid="router-nombre"
                    placeholder="Mi Router"
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    Zona Horaria
                  </label>
                  <select
                    value={formData.router.zona_horaria}
                    onChange={(e) => setFormData({
                      ...formData,
                      router: { ...formData.router, zona_horaria: e.target.value }
                    })}
                    data-testid="router-timezone"
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                  >
                    <option value="America/Mexico_City">America/Mexico_City</option>
                    <option value="America/Bogota">America/Bogota</option>
                    <option value="America/Lima">America/Lima</option>
                    <option value="America/Santiago">America/Santiago</option>
                    <option value="America/Buenos_Aires">America/Buenos_Aires</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    Subnet LAN
                  </label>
                  <input
                    type="text"
                    value={formData.router.subnet_lan}
                    onChange={(e) => setFormData({
                      ...formData,
                      router: { ...formData.router, subnet_lan: e.target.value }
                    })}
                    data-testid="router-subnet"
                    placeholder="192.168.0.0/24"
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    DNS Servers
                  </label>
                  <input
                    type="text"
                    value={formData.router.dns_servers.join(', ')}
                    onChange={(e) => setFormData({
                      ...formData,
                      router: {
                        ...formData.router,
                        dns_servers: e.target.value.split(',').map(s => s.trim())
                      }
                    })}
                    data-testid="router-dns"
                    placeholder="8.8.8.8, 8.8.4.4"
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Preview & Download */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">
                  Script Generado
                </h2>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              
              <div className="bg-gray-900 p-4 rounded-lg overflow-x-auto">
                <pre className="text-sm text-green-400 font-mono whitespace-pre">
                  {generatedScript}
                </pre>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => copyToClipboard(generatedScript)}
                  data-testid="copy-script-btn"
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  <Copy className="w-5 h-5" />
                  Copiar al Portapapeles
                </button>
              </div>

              <button
                onClick={() => navigate('/scripts')}
                data-testid="new-config-btn"
                className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Nueva Configuración
              </button>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 6 && (
          <div className="flex justify-between mt-6">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              data-testid="prev-step-btn"
              className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Anterior
            </button>

            <button
              onClick={nextStep}
              disabled={!canProceed() || loading}
              data-testid="next-step-btn"
              className="flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              {loading ? 'Generando...' : currentStep === 5 ? 'Generar Script' : 'Siguiente'}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BalanceadorWizard;
